export interface PartyTypeDef {
  value: string;
  label: string;
  icon: string;
  color: string;
  callable: boolean;
  group: string;
}

export const PARTY_TYPE_DEFINITIONS = [
  {
    value: 'seller',
    label: 'Seller',
    icon: 'Home',
    color: 'bg-emerald-100 text-emerald-700',
    callable: true,
    group: 'seller',
  },
  {
    value: 'title_co',
    label: 'Title Co.',
    icon: 'Building2',
    color: 'bg-sky-100 text-sky-600',
    callable: true,
    group: 'title_co',
  },
  {
    value: 'lender',
    label: 'Lender',
    icon: 'Landmark',
    color: 'bg-violet-100 text-violet-600',
    callable: true,
    group: 'lender',
  },
  {
    value: 'vendor',
    label: 'Vendor',
    icon: 'HardHat',
    color: 'bg-amber-100 text-amber-700',
    callable: true,
    group: 'operator',
  },
  {
    value: 'contractor',
    label: 'Contractor',
    icon: 'HardHat',
    color: 'bg-orange-100 text-orange-600',
    callable: true,
    group: 'operator',
  },
  {
    value: 'referral_partner',
    label: 'Referral Partner',
    icon: 'Handshake',
    color: 'bg-pink-100 text-pink-600',
    callable: true,
    group: 'referral_partner',
  },
  {
    value: 'internal',
    label: 'Internal',
    icon: 'MessagesSquare',
    color: 'bg-slate-100 text-slate-600',
    callable: false,
    group: 'internal',
  },
  {
    value: 'other',
    label: 'Other',
    icon: 'CircleDashed',
    color: 'bg-gray-100 text-gray-500',
    callable: true,
    group: 'other',
  },
  // Positioned after 'other' to stay in lockstep with the live party_type enum
  // order (pg_enum appended 'operator' late; see SCHEMA_PARTY_TYPE_VALUES).
  {
    value: 'operator',
    label: 'Operator',
    icon: 'HardHat',
    color: 'bg-teal-100 text-teal-700',
    callable: true,
    group: 'operator',
  },
  {
    value: 'buyer',
    label: 'Buyer',
    icon: 'HandCoins',
    color: 'bg-lime-100 text-lime-700',
    callable: true,
    group: 'buyer',
  },
  {
    value: 'title_attorney',
    label: 'Title Attorney',
    icon: 'Building2',
    color: 'bg-sky-100 text-sky-600',
    callable: true,
    group: 'title_co',
  },
  {
    value: 'title_agent',
    label: 'Title Agent',
    icon: 'Building2',
    color: 'bg-sky-100 text-sky-600',
    callable: true,
    group: 'title_co',
  },
  {
    value: 'listing_agent',
    label: 'Listing Agent',
    icon: 'UserCheck',
    color: 'bg-indigo-100 text-indigo-700',
    callable: true,
    group: 'buyer_agent',
  },
  {
    value: 'buyer_agent',
    label: 'Buyer Agent',
    icon: 'UserCheck',
    color: 'bg-indigo-100 text-indigo-700',
    callable: true,
    group: 'buyer_agent',
  },
  {
    value: 'ai_employee',
    label: 'AI Employee',
    icon: 'Bot',
    color: 'bg-purple-100 text-purple-700',
    callable: false,
    group: 'ai_employee',
  },
  {
    value: 'county_contact',
    label: 'County Contacts',
    icon: 'Landmark',
    color: 'bg-amber-100 text-amber-700',
    callable: true,
    group: 'county_contact',
  },
  {
    value: 'utility_provider',
    label: 'Utility Providers',
    icon: 'Zap',
    color: 'bg-cyan-100 text-cyan-700',
    callable: true,
    group: 'utility_provider',
  },
] as const satisfies readonly PartyTypeDef[];

export type PartyTypeValue = (typeof PARTY_TYPE_DEFINITIONS)[number]['value'];
export type PartyTypeGroup = (typeof PARTY_TYPE_DEFINITIONS)[number]['group'];

export const PARTY_TYPE_VALUES = PARTY_TYPE_DEFINITIONS.map((definition) => definition.value) as [
  PartyTypeValue,
  ...PartyTypeValue[],
];

export const PARTY_TYPE_LABELS_BY_VALUE: Record<PartyTypeValue, string> = Object.fromEntries(
  PARTY_TYPE_DEFINITIONS.map((definition) => [definition.value, definition.label]),
) as Record<PartyTypeValue, string>;

export const PARTY_TYPE_COLORS_BY_VALUE: Record<PartyTypeValue, string> = Object.fromEntries(
  PARTY_TYPE_DEFINITIONS.map((definition) => [definition.value, definition.color]),
) as Record<PartyTypeValue, string>;

export const PARTY_TYPE_DEFINITION_BY_VALUE: Record<PartyTypeValue, (typeof PARTY_TYPE_DEFINITIONS)[number]> =
  Object.fromEntries(PARTY_TYPE_DEFINITIONS.map((definition) => [definition.value, definition])) as Record<
    PartyTypeValue,
    (typeof PARTY_TYPE_DEFINITIONS)[number]
  >;

/**
 * PICKER ORDER, decoupled from pg_enum order.
 *
 * `PARTY_TYPE_DEFINITIONS` is ordered to match the LIVE `party_type` pg_enum
 * exactly, because `PARTY_TYPE_VALUES` feeds `pgEnum(...)` and the lockstep test
 * asserts the two agree. That order is a database fact: live history appended
 * `operator` late, so it sits ninth.
 *
 * Picker order is a product decision and must not move just because the database
 * enum does. `operator` reads next to `vendor` and `contractor`, which is where
 * it has always been shown. Keeping these two orders in separate constructs is
 * what stops a schema-history change from silently reordering a customer-facing
 * dropdown.
 *
 * Both lists must contain exactly the same values; `party-types.test.ts` asserts
 * that, so a new party type cannot be added to one and forgotten in the other.
 */
export const PARTY_TYPE_DISPLAY_ORDER = [
  'seller',
  'title_co',
  'lender',
  'vendor',
  'contractor',
  'operator',
  'referral_partner',
  'internal',
  'other',
  'buyer',
  'title_attorney',
  'title_agent',
  'listing_agent',
  'buyer_agent',
  'ai_employee',
  'county_contact',
  'utility_provider',
] as const satisfies readonly PartyTypeValue[];

/**
 * The definitions in picker order. Anything rendering a LIST of party types for a
 * human should use this; anything mirroring the database enum should use
 * `PARTY_TYPE_DEFINITIONS`.
 */
export const PARTY_TYPE_DISPLAY_DEFINITIONS = PARTY_TYPE_DISPLAY_ORDER.map(
  (value) => PARTY_TYPE_DEFINITION_BY_VALUE[value],
);

export const NON_CALLABLE_PARTY_TYPE_VALUES = PARTY_TYPE_DEFINITIONS
  .filter((definition) => !definition.callable)
  .map((definition) => definition.value) as PartyTypeValue[];

const GROUP_LABELS: Partial<Record<PartyTypeGroup, string>> = {
  seller: 'Sellers',
  buyer: 'Buyers',
  lender: 'Lenders',
  buyer_agent: 'Agents',
  operator: 'Operators',
  title_co: 'Title Cos',
  referral_partner: 'Wholesalers',
  other: 'Other',
  ai_employee: 'AI Employees',
  county_contact: 'County Contacts',
  utility_provider: 'Utility Providers',
};

export const INBOX_CONTACT_TYPE_GROUP_ORDER = [
  'seller',
  'buyer',
  'lender',
  'buyer_agent',
  'operator',
  'title_co',
  'referral_partner',
  'county_contact',
  'utility_provider',
  'ai_employee',
  'other',
] as const satisfies readonly PartyTypeGroup[];

export type InboxContactTypeValue = (typeof INBOX_CONTACT_TYPE_GROUP_ORDER)[number];

function definitionForGroup(group: PartyTypeGroup) {
  return (
    PARTY_TYPE_DEFINITIONS.find((definition) => definition.value === group) ??
    PARTY_TYPE_DEFINITIONS.find((definition) => definition.group === group)
  );
}

export const INBOX_CONTACT_TYPE_OPTIONS = INBOX_CONTACT_TYPE_GROUP_ORDER.map((group) => {
  const definition = definitionForGroup(group);
  if (!definition) throw new Error(`Missing party type definition for group ${group}`);
  return {
    value: definition.value as InboxContactTypeValue,
    label: GROUP_LABELS[group] ?? definition.label,
    internal: definition.value === 'ai_employee' ? true : undefined,
  };
});

export const PARTY_TYPE_GROUP_MEMBERS: Record<PartyTypeGroup, PartyTypeValue[]> = PARTY_TYPE_DEFINITIONS.reduce(
  (acc, definition) => {
    const group = definition.group as PartyTypeGroup;
    acc[group] = [...(acc[group] ?? []), definition.value as PartyTypeValue];
    return acc;
  },
  {} as Record<PartyTypeGroup, PartyTypeValue[]>,
);

export function partyTypeGroupMembers(value: PartyTypeValue): PartyTypeValue[] {
  const group = PARTY_TYPE_DEFINITION_BY_VALUE[value].group as PartyTypeGroup;
  return PARTY_TYPE_GROUP_MEMBERS[group] ?? [value];
}
